import { useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Container } from "@/components/site/Container";
import { ButtonLink } from "@/components/site/Button";
import { Phone, MessageSquare, Star, ShieldCheck } from "lucide-react";

const PHONE = "480-941-5373";
const TEL_HREF = "tel:+14809415373";
const SMS_HREF = "sms:+14809415373";

export const Route = createFileRoute("/thank-you")({
  head: () => ({
    meta: [
      { title: "Thank You — Durafoam Roofing" },
      {
        name: "description",
        content:
          "Your request has been received. A Durafoam Roofing team member will call you soon to schedule your free roof inspection.",
      },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: ThankYouPage,
});

const STEPS = [
  {
    n: 1,
    title: "We review your request",
    body: "We'll look over the information you submitted.",
  },
  {
    n: 2,
    title: "We call you soon",
    body: "A team member will follow up to learn more about your roof concern.",
  },
  {
    n: 3,
    title: "We schedule your free inspection",
    body: "We'll find a time that works for you.",
  },
  {
    n: 4,
    title: "You get an honest recommendation",
    body: "If work is needed, we'll explain your options clearly and provide a written estimate.",
  },
];

const TRUST_POINTS = [
  "4.8-Star Google Rated",
  "84 Google Reviews",
  "Family-Owned Since 1989",
  "Foam, tile, shingle, leak repair, recoating & maintenance",
];

const REVIEWS = [
  {
    quote:
      "Durafoam recoated our foam roof and the crew was professional from quote to cleanup. Job done right, on time, and the price matched the estimate.",
    name: "Phoenix homeowner",
  },
  {
    quote:
      "Honest assessment — they didn't try to upsell. Fixed our leak quickly and the roof has been dry through monsoon season.",
    name: "Scottsdale homeowner",
  },
  {
    quote:
      "Family-run, easy to reach, and they stand behind their work. Highly recommend for foam roof recoating in the Valley.",
    name: "Mesa homeowner",
  },
];

type LovableWindow = Window & {
  dataLayer?: Array<Record<string, unknown>>;
};

function ThankYouPage() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const w = window as LovableWindow;
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({
      event: "lead_form_submission",
      page: "/thank-you",
    });
  }, []);

  function trackPhoneClick(location: string) {
    if (typeof window === "undefined") return;
    const w = window as LovableWindow;
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({ event: "phone_call_click", location });
  }

  function trackTextClick(location: string) {
    if (typeof window === "undefined") return;
    const w = window as LovableWindow;
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({ event: "text_message_click", location });
  }

  return (
    <>
      {/* Hero */}
      <section className="relative bg-primary text-white">
        <Container className="py-14 lg:py-20">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="text-3xl font-bold leading-[1.15] text-white sm:text-4xl lg:text-5xl">
              Request Received!
            </h1>
            <p className="mt-5 text-lg text-white/90">
              A Durafoam Roofing team member will call you soon to go over your
              roof concern and help schedule the best time for your free roof
              inspection.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3 sm:hidden">
              <ButtonLink
                href={TEL_HREF}
                variant="primary"
                size="lg"
                data-event="phone_call_thank_you"
                onClick={() => trackPhoneClick("thank_you_hero")}
              >
                <Phone className="h-5 w-5" aria-hidden="true" />
                Call Durafoam Roofing Now — {PHONE}
              </ButtonLink>
              <ButtonLink
                href={SMS_HREF}
                variant="ghost-light"
                size="lg"
                data-event="text_message_thank_you"
                onClick={() => trackTextClick("thank_you_hero")}
              >
                <MessageSquare className="h-5 w-5" aria-hidden="true" />
                Text Us Now — {PHONE}
              </ButtonLink>
            </div>
            <div className="mt-8 hidden items-center justify-center gap-2 text-xl font-semibold text-white sm:flex">
              <Phone className="h-5 w-5" aria-hidden="true" />
              {PHONE}
            </div>
            <p className="mt-5 text-sm text-white/80">
              If you have an active leak, storm damage, or an urgent roof
              concern, calling now is the fastest way to reach us.
            </p>
          </div>
        </Container>
      </section>

      {/* What Happens Next */}
      <section className="bg-background py-14 lg:py-20">
        <Container>
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-2xl font-bold text-primary sm:text-3xl">
              What Happens Next
            </h2>
            <p className="mt-3 text-foreground/75">
              A simple, no-pressure process from request to recommendation.
            </p>
          </div>
          <div className="mx-auto mt-10 grid max-w-5xl grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s) => (
              <div
                key={s.n}
                className="rounded-2xl border border-border bg-white p-6 shadow-[0_10px_30px_-18px_rgba(15,26,66,0.18)]"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent text-base font-bold text-white">
                  {s.n}
                </div>
                <h3 className="mt-4 text-lg font-semibold text-primary">
                  {s.title}
                </h3>
                <p className="mt-2 text-[15px] leading-relaxed text-foreground/80">
                  {s.body}
                </p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Trust */}
      <section className="bg-[var(--surface)] py-14 lg:py-20">
        <Container>
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-2xl font-bold text-primary sm:text-3xl">
              Trusted by Phoenix-Area Homeowners Since 1989
            </h2>
          </div>
          <ul className="mx-auto mt-8 grid max-w-4xl grid-cols-1 gap-3 sm:grid-cols-2">
            {TRUST_POINTS.map((p) => (
              <li
                key={p}
                className="flex items-start gap-3 rounded-xl bg-white px-4 py-3 text-[15px] text-foreground/85 shadow-sm"
              >
                <ShieldCheck
                  className="mt-0.5 h-5 w-5 shrink-0 text-accent"
                  aria-hidden="true"
                />
                {p}
              </li>
            ))}
          </ul>
          <div className="mx-auto mt-10 grid max-w-5xl grid-cols-1 gap-5 md:grid-cols-3">
            {REVIEWS.map((r) => (
              <figure
                key={r.name}
                className="flex h-full flex-col justify-between rounded-2xl border border-border bg-white p-6"
              >
                <div>
                  <span className="sr-only">5 out of 5 stars</span>
                  <div className="flex gap-1 text-accent" aria-hidden="true">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} className="h-4 w-4 fill-current" />
                    ))}
                  </div>
                </div>
                <blockquote className="mt-3 text-[15px] leading-relaxed text-foreground/85">
                  &ldquo;{r.quote}&rdquo;
                </blockquote>
                <figcaption className="mt-4 text-sm font-semibold text-primary">
                  — {r.name}
                </figcaption>
              </figure>
            ))}
          </div>
        </Container>
      </section>

      {/* Reassurance */}
      <section className="bg-background py-14 lg:py-20">
        <Container>
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-2xl font-bold text-primary sm:text-3xl">
              You're in Good Hands
            </h2>
            <p className="mt-4 text-[17px] leading-relaxed text-foreground/85">
              Durafoam Roofing has been helping Phoenix-area homeowners with
              foam roofs, roof leaks, tile roofs, roof repairs, and recoating
              since 1989. Our goal is to give you an honest assessment and help
              you understand the best next step for your roof.
            </p>
          </div>
        </Container>
      </section>

      {/* Final CTA */}
      <section className="bg-[var(--primary-deep)] py-14 text-white lg:py-16">
        <Container className="text-center">
          <h2 className="mx-auto max-w-3xl text-2xl font-bold text-white sm:text-3xl">
            Need Help Sooner?
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-white/80">
            If your roof is actively leaking or you would rather talk to someone
            now, call or text Durafoam Roofing.
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <ButtonLink
              href={TEL_HREF}
              variant="primary"
              size="lg"
              data-event="phone_call_thank_you"
              onClick={() => trackPhoneClick("thank_you_footer")}
            >
              <Phone className="h-5 w-5" aria-hidden="true" />
              Call {PHONE}
            </ButtonLink>
            <ButtonLink
              href={SMS_HREF}
              variant="ghost-light"
              size="lg"
              data-event="text_message_thank_you"
              onClick={() => trackTextClick("thank_you_footer")}
            >
              <MessageSquare className="h-5 w-5" aria-hidden="true" />
              Text {PHONE}
            </ButtonLink>
          </div>
        </Container>
      </section>
    </>
  );
}
